import type { CreateLinkType, LinkType, Page, UpdateLinkType } from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';

const base = adminPath('/link-types');

/** `GET /admin/v1/link-types` (small; returned as one page with `nextCursor: null`). */
export function useLinkTypes() {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.linkTypes.list(),
    queryFn: ({ signal }) => request<Page<LinkType>>('GET', base, { signal }),
    select: (page) => page.items,
    staleTime: 5 * 60_000,
  });
}

export function useLinkType(linkTypeId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.linkTypes.detail(linkTypeId ?? ''),
    queryFn: ({ signal }) => request<LinkType>('GET', `${base}/${linkTypeId}`, { signal }),
    enabled: linkTypeId !== undefined,
  });
}

export function useCreateLinkType() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateLinkType) => request<LinkType>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.linkTypes.all),
  });
}

export function useUpdateLinkType() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateLinkType }) =>
      request<LinkType>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: () => invalidate(keys.linkTypes.all),
  });
}

export function useDeleteLinkType() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${base}/${id}`),
    onSuccess: () => invalidate(keys.linkTypes.all),
  });
}
